import React, { useState, useEffect, useRef } from "react";
import {
  SelectField,
  TextAreaField,
  UploadBox,
  ChipButton,
  PrimaryButton,
} from "./components";
import { useToast } from "../../../../hooks/useToast";
import providersService from "../../../../api/services/providersService";

import {
  assistantServiceOptions as serviceOptions,
  normalizeAssistantServices,
  serializeAssistantServices,
} from "../../../../data/assistantServiceOptions";
import {
  NIGERIA_STATE_OPTIONS as stateOptions,
  normalizeNigeriaState,
} from "../../../../constants/nigeriaStates";
import {
  ACCEPTED_IMAGE_ACCEPT,
  MAX_IMAGE_SIZE_MB,
  validateImageFile,
} from "../../../../constants/imageUpload";
import { resizeImageForUpload } from "../../../../utils/imageResize";


function resolveProfilePicture(profile) {
  if (!profile) return "";
  const pic = profile.profilePicture || profile.profile_picture || profile.avatar || "";
  if (!pic) return "";
  if (typeof pic === "string") return pic;
  if (pic.url) return pic.url;
  return "";
}

// Resolves whatever the profile has stored to the exact state name the
// providers endpoint accepts; anything unrecognised becomes "" so the panel
// asks for a fresh selection instead of resubmitting a value that 400s.
function normalizeStateValue(value) {
  return normalizeNigeriaState(typeof value === "string" ? value : String(value ?? ""));
}

export default function PersonalPanel({ profile, onRequestReview }) {
  const toast = useToast();
  const avatarInputRef = useRef(null);

  const [personalForm, setPersonalForm] = useState({
    state: "",
    bio: "",
    services: [],
  });
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [isSavingPersonal, setIsSavingPersonal] = useState(false);

  useEffect(() => {
    if (profile) {
      setPersonalForm({
        state: normalizeStateValue(
          profile.state ||
            profile.stateName ||
            profile.state_name ||
            profile.location?.state ||
            profile.address?.state ||
            profile.cityState
        ),
        bio:
          profile.bio ||
          profile.customBio ||
          profile.description ||
          "",
        services: normalizeAssistantServices(profile.services),
      });
      setAvatarPreview(resolveProfilePicture(profile));
    }
  }, [profile]);

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const validationError = validateImageFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }
      // Resize before upload so a full-resolution camera photo doesn't turn a
      // one-field save into a multi-megabyte transfer.
      const uploadFile = await resizeImageForUpload(file);
      setAvatarFile(uploadFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(uploadFile);
    }
  };

  const validatePersonalForm = () => {
    if (!personalForm.bio.trim()) {
      toast.error("Please add a short description.");
      return false;
    }
    if (!personalForm.state) {
      toast.error("Please select your state of residence.");
      return false;
    }
    if (!personalForm.services.length) {
      toast.error("Please select at least one service.");
      return false;
    }
    if (!avatarPreview && !avatarFile) {
      toast.error("Please upload a profile photo.");
      return false;
    }
    return true;
  };

  const handleSaveClick = () => {
    if (!validatePersonalForm()) return;

    onRequestReview("personal", async () => {
      setIsSavingPersonal(true);
      try {
        await providersService.updateProfile({
          bio: personalForm.bio.trim(),
          state: personalForm.state,
          services: serializeAssistantServices(personalForm.services),
          profilePicture: avatarFile || undefined,
        });
        toast.success("Personal information updated successfully.");
        setAvatarFile(null);
        return true;
      } catch (error) {
        toast.apiError(error, "Unable to update personal information.");
        return false;
      } finally {
        setIsSavingPersonal(false);
      }
    });
  };

  const toggleService = (service) => {
    setPersonalForm((prev) => {
      const active = prev.services.includes(service);
      const updated = active
        ? prev.services.filter((item) => item !== service)
        : [...prev.services, service];
      return { ...prev, services: updated };
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <div className="space-y-5">
          <SelectField
            label="State of Residence"
            value={personalForm.state}
            onChange={(value) => {
              setPersonalForm((prev) => ({ ...prev, state: value }));
            }}
            options={stateOptions}
            placeholder="Select state"
          />
          <TextAreaField
            label="Custom Bio"
            value={personalForm.bio}
            onChange={(value) => {
              setPersonalForm((prev) => ({ ...prev, bio: value }));
            }}
            rows={5}
            placeholder="Tell us about your tour style."
          />
        </div>

        <div className="space-y-5">
          <UploadBox
            title="Add Profile Picture"
            helper={`Upload a clear photo of yourself. JPG, JPEG or PNG, up to ${MAX_IMAGE_SIZE_MB}MB.`}
            fileName={avatarFile?.name || avatarPreview ? "Profile picture selected" : ""}
            buttonLabel="Upload Photo"
            onClick={() => avatarInputRef.current?.click()}
            preview={avatarPreview}
            previewAlt={profile?.fullName || profile?.name || "Assistant"}
            className="min-h-[280px]"
          >
            <input
              ref={avatarInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_ACCEPT}
              className="hidden"
              onChange={handleAvatarChange}
            />
          </UploadBox>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-base font-semibold text-[#344054]">Services</label>
              <span className="text-sm font-medium text-[#667085]">
                {personalForm.services.length} selected
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {serviceOptions.map((service) => (
                <ChipButton
                  key={service}
                  active={personalForm.services.includes(service)}
                  onClick={() => toggleService(service)}
                >
                  {service}
                </ChipButton>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <PrimaryButton
          onClick={handleSaveClick}
          disabled={isSavingPersonal}
          label={isSavingPersonal ? "Saving..." : "Save Changes"}
        />
      </div>
    </div>
  );
}
